import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, RefreshCw, TrendingDown, AlertCircle, Users, ShoppingBag, Calendar, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

const REASON_COLORS = {
  'Price High': '#ef4444',
  'Design Not Suitable': '#f97316',
  'Waiting for Budget': '#eab308',
  'Competitor Purchased': '#8b5cf6',
  'Stock Not Available': '#3b82f6',
  'Occasion Cancelled': '#6b7280',
  'Other': '#a3a3a3',
};

const LossReasonAnalytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [activeTab, setActiveTab] = useState('reasons');
  const [expandedReason, setExpandedReason] = useState(null);
  const [expandedSalesman, setExpandedSalesman] = useState(null);
  const tabsSectionRef = useRef(null);

  const scrollToReasonTab = (reason = null) => {
    setActiveTab('reasons');
    if (reason) setExpandedReason(reason);
    setTimeout(() => tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/analytics/loss-reasons?period=${period}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [period]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
    </div>
  );

  const summary = data?.summary || {};
  const byReason = data?.by_reason || [];
  const bySalesman = data?.by_salesman || [];
  const byProduct = data?.by_product || [];
  const monthlyTrend = data?.monthly_trend || [];
  const otherComments = data?.other_comments || [];

  const tabs = [
    { id: 'reasons', label: 'By Reason', icon: AlertCircle },
    { id: 'salesmen', label: 'By Salesman', icon: Users },
    { id: 'products', label: 'By Product', icon: ShoppingBag },
    { id: 'trends', label: 'Trends', icon: Calendar },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="loss-reason-analytics-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-300" /> Loss Analysis
              </h1>
              <p className="text-xs text-emerald-200">{summary.total_lost || 0} leads lost</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36 h-8 bg-emerald-700 border-emerald-600 text-white text-xs" data-testid="period-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Summary Cards */}
        <div className="px-4 pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="loss-summary">
            <div
              className="bg-white rounded-lg border p-3 cursor-pointer hover:border-red-300 hover:shadow-sm active:scale-95 transition-all"
              onClick={() => navigate('/lead-sessions?lead_status=lost')}
              data-testid="total-lost-card"
            >
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Total Lost</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{summary.total_lost || 0}</p>
              <p className="text-xs text-gray-400">leads</p>
            </div>
            <div
              className="bg-white rounded-lg border p-3 cursor-pointer hover:border-red-300 hover:shadow-sm active:scale-95 transition-all"
              onClick={() => navigate('/lead-sessions?lead_status=lost&sort_by=estimated_value&sort_order=desc')}
              data-testid="value-lost-card"
            >
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Value Lost</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{fmt(summary.total_lost_value)}</p>
              <p className="text-xs text-gray-400">potential revenue</p>
            </div>
            <div
              className="bg-white rounded-lg border p-3 cursor-pointer hover:border-red-300 hover:shadow-sm active:scale-95 transition-all"
              onClick={() => scrollToReasonTab(summary.top_reason)}
              data-testid="top-reason-card"
            >
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Top Reason</p>
              <p className="text-lg font-bold text-gray-800 mt-1 truncate">{summary.top_reason || '-'}</p>
              <p className="text-xs text-gray-400">{summary.top_reason_count || 0} leads</p>
            </div>
            <div className="bg-white rounded-lg border p-3">
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Avg Loss Value</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{summary.total_lost > 0 ? fmt(summary.total_lost_value / summary.total_lost) : '₹0'}</p>
              <p className="text-xs text-gray-400">per lead</p>
            </div>
          </div>
        </div>

        {/* Reason Distribution Bar */}
        {byReason.length > 0 && (
          <div className="px-4 pt-3">
            <div className="bg-white rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Loss Reason Distribution</p>
              <div className="flex h-6 rounded-full overflow-hidden gap-0.5">
                {byReason.map((r) => {
                  const pct = summary.total_lost > 0 ? (r.count / summary.total_lost) * 100 : 0;
                  if (pct < 1) return null;
                  return (
                    <div key={r.reason} className="transition-all cursor-pointer hover:opacity-80"
                      style={{ width: `${pct}%`, backgroundColor: REASON_COLORS[r.reason] || '#6b7280' }}
                      title={`${r.reason}: ${r.count} (${Math.round(pct)}%)`}
                      onClick={() => setExpandedReason(expandedReason === r.reason ? null : r.reason)}
                    />
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {byReason.map((r) => (
                  <span key={r.reason} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: REASON_COLORS[r.reason] || '#6b7280' }} />
                    {r.reason}: {r.count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="px-4 pt-3" ref={tabsSectionRef}>
          <div className="flex bg-white rounded-lg border overflow-hidden">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${
                  activeTab === tab.id ? 'bg-red-50 text-red-700 border-b-2 border-red-500' : 'text-gray-500 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 pt-3 pb-4">
          {/* By Reason Tab */}
          {activeTab === 'reasons' && (
            <div className="space-y-3" data-testid="reasons-tab">
              {byReason.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No lost leads data available</p>
                </div>
              ) : byReason.map((reason) => {
                const pct = summary.total_lost > 0 ? Math.round((reason.count / summary.total_lost) * 100) : 0;
                const valuePct = summary.total_lost_value > 0 ? Math.round((reason.value / summary.total_lost_value) * 100) : 0;
                const isExpanded = expandedReason === reason.reason;
                return (
                  <div key={reason.reason} className="bg-white rounded-lg border overflow-hidden" data-testid={`reason-card-${reason.reason}`}>
                    <button
                      onClick={() => setExpandedReason(isExpanded ? null : reason.reason)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: REASON_COLORS[reason.reason] || '#6b7280' }} />
                        <div className="text-left">
                          <p className="font-semibold text-sm text-gray-900">{reason.reason}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{reason.count} lead{reason.count !== 1 ? 's' : ''} ({pct}%)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-red-600">{fmt(reason.value)}</p>
                          <p className="text-[10px] text-gray-400">{valuePct}% of total</p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {isExpanded && reason.leads && reason.leads.length > 0 && (
                      <div className="border-t divide-y bg-gray-50">
                        {reason.leads.slice(0, 10).map((lead, idx) => (
                          <div key={idx} className="px-4 py-2.5 text-xs">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-900">{lead.product_name}</span>
                                {lead.main_group && <span className="text-gray-400 ml-2">{lead.main_group}</span>}
                              </div>
                              <span className="font-bold text-red-600">{fmt(lead.estimated_value)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-gray-500">
                              <span>{lead.client_name}</span>
                              <span>|</span>
                              <span>{lead.salesman_name}</span>
                              {lead.reason_comment && (
                                <>
                                  <span>|</span>
                                  <span className="italic text-gray-400">"{lead.reason_comment}"</span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        {reason.leads.length > 10 && (
                          <div className="px-4 py-2 text-xs text-gray-400 text-center">
                            +{reason.leads.length - 10} more leads
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* By Salesman Tab */}
          {activeTab === 'salesmen' && (
            <div className="space-y-3" data-testid="salesmen-tab">
              {bySalesman.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No salesman loss data</p>
                </div>
              ) : bySalesman.map((s) => {
                const isExpanded = expandedSalesman === s.salesman_id;
                const reasonEntries = Object.entries(s.reasons || {}).sort((a, b) => b[1] - a[1]);
                return (
                  <div key={s.salesman_id} className="bg-white rounded-lg border overflow-hidden" data-testid={`salesman-loss-${s.salesman_id}`}>
                    <button
                      onClick={() => setExpandedSalesman(isExpanded ? null : s.salesman_id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-sm text-gray-900">{s.salesman_name}</p>
                          <p className="text-xs text-gray-500">{s.total_lost} lost lead{s.total_lost !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-bold text-red-600">{fmt(s.total_value)}</p>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {isExpanded && reasonEntries.length > 0 && (
                      <div className="border-t px-4 py-3 bg-gray-50">
                        <p className="text-xs font-medium text-gray-600 mb-2">Loss Reason Breakdown</p>
                        <div className="space-y-2">
                          {reasonEntries.map(([reason, count]) => {
                            const pct = s.total_lost > 0 ? Math.round((count / s.total_lost) * 100) : 0;
                            return (
                              <div key={reason}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-gray-700 font-medium">{reason}</span>
                                  <span className="text-gray-500">{count} ({pct}%)</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: REASON_COLORS[reason] || '#6b7280' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* By Product Tab */}
          {activeTab === 'products' && (
            <div className="space-y-3" data-testid="products-tab">
              {byProduct.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No product loss data</p>
                </div>
              ) : byProduct.map((p) => {
                const reasonEntries = Object.entries(p.reasons || {}).sort((a, b) => b[1] - a[1]);
                return (
                  <div key={p.main_group} className="bg-white rounded-lg border p-4" data-testid={`product-loss-${p.main_group}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm text-gray-900">{p.main_group}</p>
                        <p className="text-xs text-gray-500">{p.count} lost lead{p.count !== 1 ? 's' : ''}</p>
                      </div>
                      <p className="font-bold text-red-600">{fmt(p.value)}</p>
                    </div>
                    {reasonEntries.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {reasonEntries.map(([reason, count]) => (
                          <span key={reason} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border"
                            style={{ backgroundColor: `${REASON_COLORS[reason] || '#6b7280'}15`, borderColor: `${REASON_COLORS[reason] || '#6b7280'}40`, color: REASON_COLORS[reason] || '#6b7280' }}>
                            {reason}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Trends Tab */}
          {activeTab === 'trends' && (
            <div className="space-y-3" data-testid="trends-tab">
              {monthlyTrend.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No trend data available</p>
                </div>
              ) : (
                <>
                  {/* Visual bar chart */}
                  <div className="bg-white rounded-lg border p-4">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Monthly Loss Trend</p>
                    <div className="space-y-3">
                      {monthlyTrend.map((m) => {
                        const maxVal = Math.max(...monthlyTrend.map(t => t.value));
                        const barWidth = maxVal > 0 ? (m.value / maxVal) * 100 : 0;
                        const monthLabel = new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
                        return (
                          <div key={m.month}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium text-gray-700 w-16">{monthLabel}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">{m.count} leads</span>
                                <span className="font-bold text-red-600">{fmt(m.value)}</span>
                              </div>
                            </div>
                            <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all"
                                style={{ width: `${barWidth}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Monthly breakdown */}
                  {monthlyTrend.slice().reverse().map((m) => {
                    const reasonEntries = Object.entries(m.reasons || {}).sort((a, b) => b[1] - a[1]);
                    const monthLabel = new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                    return (
                      <div key={m.month} className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-sm text-gray-900">{monthLabel}</p>
                          <div className="text-right">
                            <p className="font-bold text-red-600">{fmt(m.value)}</p>
                            <p className="text-[10px] text-gray-400">{m.count} leads</p>
                          </div>
                        </div>
                        {reasonEntries.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {reasonEntries.map(([reason, count]) => (
                              <span key={reason} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                                style={{ backgroundColor: `${REASON_COLORS[reason] || '#6b7280'}15`, color: REASON_COLORS[reason] || '#6b7280' }}>
                                {reason}: {count}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* Other Comments Section */}
              {otherComments.length > 0 && (
                <div className="bg-white rounded-lg border p-4">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Custom Loss Reasons ("Other")
                  </p>
                  <div className="space-y-2">
                    {otherComments.map((c, i) => (
                      <div key={i} className="pl-3 border-l-2 border-gray-200 text-xs">
                        <p className="text-gray-800 italic">"{c.comment}"</p>
                        <p className="text-gray-400 mt-0.5">{c.product_name} | {c.salesman_name} | {fmt(c.estimated_value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LossReasonAnalytics;
